import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Drawer, Tag } from "@stonelog/stonelog-design-system";
import machineError from "@assets/imgs/machine-error.svg";
import {
  adminStoneEntregaSelector,
  getAdminStoneEntregaArquivos,
  postAdminStoneEntregaCancelamento,
  resetAdminStoneEntregaArquivos,
  resetAdminStoneEntregaCancelamento,
} from "../../../../features/adminStoneEntrega/adminStoneEntregaSlice";
import CancelDialog from "./CancelDialog";
import DeliveryTimeline from "./DeliveryTimeline";
import {
  PATHWAY_TYPE_LABELS,
  formatDateTime,
  formatEnumLabel,
  getImageHashes,
  getStatusTagType,
  isFinalStatus,
} from "../constants";
import { useNotify } from "@hooks/useNotify";

import * as S from "../styles";

const CANCEL_ERROR_MESSAGES = {
  400: "Informe o motivo do cancelamento.",
  404: "Delivery não encontrada.",
  422: "Esta delivery não pode mais ser cancelada (status final ou sem etapa ativa).",
};

const Field = ({ label, children }) => (
  <S.BodyTopContent>
    <p>{label}</p>
    <span>{children || "-"}</span>
  </S.BodyTopContent>
);

// Drawer da delivery selecionada: cabeçalho, ineficiências, etapas e a ação de cancelar.
const DeliveryDrawer = ({ open, onClose, delivery }) => {
  const dispatch = useDispatch();
  const notify = useNotify();
  const { cancelamentoStatus, arquivos } = useSelector(adminStoneEntregaSelector);

  const [openPathways, setOpenPathways] = useState({});
  const [cancelOpen, setCancelOpen] = useState(false);
  const [motivo, setMotivo] = useState("");

  const canCancel = Boolean(delivery) && !isFinalStatus(delivery.status);

  // Fotos das evidências: o front não chama a deliveries-api direto (GET /files exige o
  // header token), então pede ao back ao abrir o drawer. Os hashes só mudam ao trocar de delivery.
  const imageHashes = useMemo(() => getImageHashes(delivery), [delivery]);
  const imageHashesKey = imageHashes.join(",");

  useEffect(() => {
    if (!open) return;
    const faltando = imageHashes.filter((hash) => !(hash in arquivos));
    if (faltando.length > 0) dispatch(getAdminStoneEntregaArquivos(faltando));
    // arquivos fica de fora de propósito: só reage a abrir o drawer ou trocar de delivery
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, delivery?.id, imageHashesKey]);

  const handleClose = () => {
    onClose();
    setOpenPathways({});
    dispatch(resetAdminStoneEntregaArquivos()); // imagens em base64 pesam: descarta ao fechar
  };

  const handleTogglePathway = (pathwayId, nextOpen) =>
    setOpenPathways((prev) => ({ ...prev, [pathwayId]: nextOpen }));

  const closeCancelDialog = () => {
    setCancelOpen(false);
    setMotivo("");
    dispatch(resetAdminStoneEntregaCancelamento());
  };

  // Depois do sucesso o slice rebusca a delivery, então a linha e o drawer se atualizam sozinhos
  const handleCancel = async () => {
    const trimmed = motivo.trim();
    if (!trimmed) return;

    const result = await dispatch(
      postAdminStoneEntregaCancelamento({ deliveryId: delivery.id, motivo: trimmed })
    );

    if (postAdminStoneEntregaCancelamento.fulfilled.match(result)) {
      notify.success(`Delivery ${delivery.id} cancelada com sucesso!`);
      closeCancelDialog();
    } else {
      const status = result.payload?.status;
      notify.error(
        CANCEL_ERROR_MESSAGES[status] || "Não foi possível cancelar a delivery. Tente novamente."
      );
      dispatch(resetAdminStoneEntregaCancelamento());
    }
  };

  return (
    <>
      <CancelDialog
        open={cancelOpen}
        deliveryId={delivery?.id}
        motivo={motivo}
        onChangeMotivo={setMotivo}
        loading={cancelamentoStatus === "pending"}
        onConfirm={handleCancel}
        onClose={closeCancelDialog}
      />

      <Drawer.Root
        placement="right"
        width="min(398px, 100vw)"
        open={open}
        onClose={handleClose}
        mask={true}
        title={
          <Drawer.Header
            title={`Delivery ${delivery?.id || ""}`}
            date={new Date()}
          />
        }
      >
        <Drawer.Body>
          {delivery ? (
            <S.AllBodyContainer>
              <S.BodyTopContainer>
                <S.BodyTopContent>
                  <p>Status</p>
                  <Tag text={formatEnumLabel(delivery.status)} type={getStatusTagType(delivery.status)} />
                </S.BodyTopContent>
                <Field label="Tracking code">{delivery.tracking_code}</Field>
                <Field label="Polo atual">{delivery.polo_atual?.hub_name}</Field>
                <Field label="Responsável pela coleta">{delivery.responsaveis?.coleta}</Field>
                <Field label="Responsável pela entrega">{delivery.responsaveis?.entrega}</Field>
                <Field label="Responsável atual">{delivery.responsaveis?.atual}</Field>
              </S.BodyTopContainer>

              {delivery.ineficiencias?.length > 0 && (
                <S.BodySection>
                  <S.SectionTitle>Ineficiências</S.SectionTitle>
                  {delivery.ineficiencias.map((item) => (
                    <S.InefficiencyItem key={`${item.pathway_id}-${item.at}`}>
                      <span>
                        {PATHWAY_TYPE_LABELS[item.pathway_type] || formatEnumLabel(item.pathway_type)} ·{" "}
                        {formatEnumLabel(item.flow)}
                      </span>
                      <p>{item.reason}</p>
                      <small>{formatDateTime(item.at)}</small>
                    </S.InefficiencyItem>
                  ))}
                </S.BodySection>
              )}

              <S.BodySection>
                <S.SectionTitle>Etapas da delivery</S.SectionTitle>
                <DeliveryTimeline
                  delivery={delivery}
                  openPathways={openPathways}
                  onTogglePathway={handleTogglePathway}
                />
              </S.BodySection>
            </S.AllBodyContainer>
          ) : (
            <Drawer.Response
              element={<img src={machineError} alt="Sem dados" />}
              message="Não foi possível encontrar os detalhes desta delivery."
            />
          )}
        </Drawer.Body>
        <Drawer.Footer>
          <Button
            type="negative"
            width="100%"
            height="52px"
            size="large"
            disabled={!canCancel}
            onClick={() => setCancelOpen(true)}
          >
            Cancelar delivery
          </Button>
          <Button width="100%" height="52px" size="large" onClick={handleClose}>
            Fechar
          </Button>
        </Drawer.Footer>
      </Drawer.Root>
    </>
  );
};

export default DeliveryDrawer;
